import React, { useState, useEffect, useCallback } from 'react';
import { Table, Tag, DatePicker, Select, Input, Space, Drawer, Typography, Descriptions, Button, Empty } from 'antd';
import { ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { adminAuditService } from '../../services/adminService';

const { RangePicker } = DatePicker;
const { Text } = Typography;

const ACTION_COLORS: Record<string, string> = {
  create: 'green',
  update: 'blue',
  delete: 'red',
  cancel: 'red',
  status_change: 'blue',
  check_in: 'cyan',
  check_out: 'cyan',
  allocate: 'geekblue',
  extend_stay: 'geekblue',
  change_room: 'geekblue',
  discount: 'orange',
  price_change: 'orange',
  charge: 'gold',
  payment: 'gold',
  settle: 'gold',
  reprint: 'purple',
  fines: 'volcano',
};

const ACTION_LABELS: Record<string, string> = {
  create: 'Created',
  update: 'Updated',
  delete: 'Deleted',
  cancel: 'Cancelled',
  status_change: 'Status Changed',
  check_in: 'Check In',
  check_out: 'Check Out',
  allocate: 'Room Allocated',
  extend_stay: 'Stay Extended',
  change_room: 'Room Changed',
  discount: 'Discount Applied',
  price_change: 'Price Changed',
  charge: 'Charge Posted',
  payment: 'Payment Recorded',
  settle: 'Payment Settled',
  reprint: 'Receipt Reprinted',
  fines: 'Fines Saved',
};

interface AuditLog {
  id: string;
  action: string;
  entity_type: string;
  entity_id: string;
  user_id: string | null;
  user_email: string | null;
  user_name: string | null;
  user_role: string | null;
  old_values: Record<string, any> | null;
  new_values: Record<string, any> | null;
  description: string | null;
  ip_address: string | null;
  created_at: string;
}

const AuditLogPage: React.FC = () => {
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Filters
  const [entityType, setEntityType] = useState<string | undefined>();
  const [action, setAction] = useState<string | undefined>();
  const [search, setSearch] = useState('');
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null] | null>(null);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const filters: Record<string, any> = { page, limit: 50 };
      if (entityType) filters.entityType = entityType;
      if (action) filters.action = action;
      if (search) filters.search = search;
      if (dateRange?.[0]) filters.fromDate = dateRange[0].toISOString();
      if (dateRange?.[1]) filters.toDate = dateRange[1].endOf('day').toISOString();

      const result = await adminAuditService.getLogs(filters) as any;
      setLogs(result.data || []);
      setTotal(result.total || 0);
    } catch (error) {
      console.error('Failed to fetch audit logs:', error);
    } finally {
      setLoading(false);
    }
  }, [page, entityType, action, search, dateRange]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const columns: ColumnsType<AuditLog> = [
    {
      title: 'Date / Time',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (val: string) => dayjs(val).format('DD MMM YYYY, HH:mm:ss'),
    },
    {
      title: 'User',
      key: 'user',
      width: 200,
      render: (_: any, record: AuditLog) => (
        <div>
          <Text strong>{record.user_name || 'System'}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: 12 }}>{record.user_email || '-'}</Text>
        </div>
      ),
    },
    {
      title: 'Role',
      dataIndex: 'user_role',
      key: 'user_role',
      width: 120,
      render: (val: string) => val ? <Tag>{val}</Tag> : '-',
    },
    {
      title: 'Action',
      dataIndex: 'action',
      key: 'action',
      width: 150,
      render: (val: string) => (
        <Tag color={ACTION_COLORS[val] || 'default'}>
          {ACTION_LABELS[val] || val}
        </Tag>
      ),
    },
    {
      title: 'Entity',
      key: 'entity',
      width: 160,
      render: (_: any, record: AuditLog) => (
        <div>
          <Text>{record.entity_type}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: 11 }}>
            {record.entity_id.substring(0, 8)}...
          </Text>
        </div>
      ),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
  ];

  const handleRowClick = (record: AuditLog) => {
    setSelectedLog(record);
    setDrawerOpen(true);
  };

  const renderJsonDiff = (label: string, data: Record<string, any> | null) => {
    if (!data || Object.keys(data).length === 0) {
      return <Text type="secondary">No data</Text>;
    }
    return (
      <div style={{ background: '#f5f5f5', borderRadius: 6, padding: 12, fontFamily: 'monospace', fontSize: 12 }}>
        {Object.entries(data).map(([key, value]) => (
          <div key={key} style={{ marginBottom: 4 }}>
            <Text strong style={{ color: '#555' }}>{key}: </Text>
            <Text style={{ color: label === 'Before' ? '#cf1322' : '#3f8600' }}>
              {typeof value === 'object' ? JSON.stringify(value) : String(value ?? 'null')}
            </Text>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 style={{ margin: 0 }}>Audit Log</h2>
        <Button icon={<ReloadOutlined />} onClick={fetchLogs}>Refresh</Button>
      </div>

      <Space wrap style={{ marginBottom: 16 }}>
        <RangePicker
          onChange={(dates) => setDateRange(dates as [dayjs.Dayjs | null, dayjs.Dayjs | null])}
          style={{ width: 280 }}
        />
        <Select
          placeholder="Entity Type"
          allowClear
          style={{ width: 160 }}
          value={entityType}
          onChange={setEntityType}
          options={[
            { value: 'booking', label: 'Booking' },
            { value: 'pos_order', label: 'POS Order' },
            { value: 'laundry_order', label: 'Laundry Order' },
            { value: 'payment', label: 'Payment' },
            { value: 'invoice', label: 'Invoice' },
            { value: 'folio_entry', label: 'Folio Entry' },
            { value: 'menu_item', label: 'Menu Item' },
            { value: 'staff_user', label: 'Staff User' },
            { value: 'booking_fines', label: 'Booking Fines' },
          ]}
        />
        <Select
          placeholder="Action"
          allowClear
          style={{ width: 160 }}
          value={action}
          onChange={setAction}
          options={Object.entries(ACTION_LABELS).map(([value, label]) => ({ value, label }))}
        />
        <Input
          placeholder="Search description..."
          prefix={<SearchOutlined />}
          style={{ width: 240 }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onPressEnter={fetchLogs}
          allowClear
        />
      </Space>

      <Table
        rowKey="id"
        columns={columns}
        dataSource={logs}
        loading={loading}
        pagination={{
          current: page,
          total,
          pageSize: 50,
          onChange: setPage,
          showSizeChanger: false,
          showTotal: (t) => `${t} records`,
        }}
        onRow={(record) => ({
          onClick: () => handleRowClick(record),
          style: { cursor: 'pointer' },
        })}
      />

      <Drawer
        title="Audit Detail"
        open={drawerOpen}
        onClose={() => { setDrawerOpen(false); setSelectedLog(null); }}
        width={520}
      >
        {selectedLog && (
          <div>
            <Descriptions column={1} bordered size="small" style={{ marginBottom: 16 }}>
              <Descriptions.Item label="Action">
                <Tag color={ACTION_COLORS[selectedLog.action] || 'default'}>
                  {ACTION_LABELS[selectedLog.action] || selectedLog.action}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Entity">{selectedLog.entity_type}</Descriptions.Item>
              <Descriptions.Item label="Entity ID">
                <Text copyable style={{ fontSize: 12 }}>{selectedLog.entity_id}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="User">{selectedLog.user_name || 'System'}</Descriptions.Item>
              <Descriptions.Item label="Email">{selectedLog.user_email || '-'}</Descriptions.Item>
              <Descriptions.Item label="Role">{selectedLog.user_role || '-'}</Descriptions.Item>
              <Descriptions.Item label="Date/Time">
                {dayjs(selectedLog.created_at).format('DD MMM YYYY, HH:mm:ss')}
              </Descriptions.Item>
              <Descriptions.Item label="IP Address">{selectedLog.ip_address || '-'}</Descriptions.Item>
              <Descriptions.Item label="Description">{selectedLog.description || '-'}</Descriptions.Item>
            </Descriptions>

            <Typography.Title level={5}>Before</Typography.Title>
            {renderJsonDiff('Before', selectedLog.old_values)}

            <Typography.Title level={5} style={{ marginTop: 16 }}>After</Typography.Title>
            {renderJsonDiff('After', selectedLog.new_values)}
          </div>
        )}
      </Drawer>
    </div>
  );
};

export default AuditLogPage;
